// Amplify
import { Amplify, Auth } from "aws-amplify";
import React, { useEffect, useState } from "react";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";

//Frameworks
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";

//Redux
import { useDispatch } from "react-redux";
import { fetchAllProviders } from "../Store/ProvidersSlice";
import { fetchAllPharmacies } from "../Store/PharmaciesSlice";
import { DateTime } from "luxon";

//MUI
import {
  Alert,
  Box,
  Link,
  Stack,
  Snackbar,
  Typography,
  AlertTitle,
  SwipeableDrawer,
  Divider,
} from "@mui/material";
import { PersonAdd, CheckCircleOutlineOutlined } from "@mui/icons-material";

// Components
import { Loading, formatTimeWithPeriod } from "./Common";
import { fetchWaitlistByPatientID } from "../api/WaitlistEndpointRequests";
import {
  appointmentsHistory,
  getPatientAppointments,
} from "../api/AppointmentEndpointRequests";
import PatientCard from "./PatientCards/patientCards";
import DeletePatientCard from "./PatientCards/deletePatientCard";
import config from "../aws-exports";
import CustomButton from "../constants/CustomButton";
import ImportantInformation from "./ImportantInformation/ImportantInformation";
import Footer from "./footer";
import Header from "./header";

// Constants and styles
import { styles } from "./styles";
import { confirmButtonColor, darkBlue } from "../constants/ColourConstants";
import { authenticatedFetch } from "../api/utils/authenticatedFetch";

Amplify.configure(config);
function Home() {
  const [patientData, setPatientData] = useState([]);
  const [unpaidAppointmentsAlert, setUnpaidAppointmentsAlert] = useState([]);
  const [unpaidAppointments, setUnpaidAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lockedCards, setLockedCards] = useState(false);
  const [todayAppointmentAlert, setTodayAppointmentAlert] = useState([]);
  const [activeWaitListStatus, setActiveWaitListStatus] = useState([]);
  const [waitlistData, setWaitlistData] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const today = moment(new Date()).format("YYYY-MM-DD");
  const tomorrow = moment(today).add(1, "day").format("YYYY-MM-DD");
  const [paidAppointments, setPaidAppointments] = useState([]);
  const [accuroDeletePatient, setAccuroDeletePatient] = useState(false);
  const [showImportantInfo, setShowImportantInfo] = useState(false);
  const [patientLoadError, setPatientLoadError] = useState(false);
  const supportEmail = "bookings.techsupport@qdoc.ca";

  useEffect(() => {
    const getPatientCards = async () => {
      setLoading(true);
      setPatientLoadError(false);
      setPatientData([]);
      const patients = await getPatientsByUserId();
      if (Array.isArray(patients) && patients.length > 0) {
        await loadPatientCards(patients);
      }
      setLoading(false);
    };

    const fetchData = async () => {
      // Fetch patient cards and related data
      await getPatientCards();

      // Fetch providers and pharmacies only if there was no error loading patients
      if (!patientLoadError) {
        dispatch(fetchAllProviders());
        dispatch(fetchAllPharmacies());
      }
    };

    fetchData();
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // If a scroll target was provided via navigation state, scroll to it after render
  useEffect(() => {
    const targetId = location.state && location.state.scrollTo;
    if (targetId) {
      // defer to ensure DOM has rendered
      const t = setTimeout(() => {
        document
          .getElementById(targetId)
          ?.scrollIntoView({ behavior: "smooth" });
      }, 0);
      return () => clearTimeout(t);
    }
  }, [location.state]);

  useEffect(() => {
    patientData.sort((a, b) => {
      return (
        a.demographics.firstName.localeCompare(b.demographics.firstName) ||
        a.demographics.lastName.localeCompare(b.demographics.lastName)
      );
    });
  }, [patientData]);

  const getPatientsByUserId = async () => {
    const user = await Auth.currentAuthenticatedUser();
    const userId = user.attributes["sub"];
    try {
      const response = await authenticatedFetch(
        `https://${process.env.REACT_APP_ENV_ACCURO_GATEWAY}.execute-api.ca-central-1.amazonaws.com/${process.env.REACT_APP_ENV}/user-info?userId=${userId}`,
        {
          method: "GET",
          headers: {
            Accept: "*/*",
          },
        }
      );

      if (!response.ok) {
        setPatientLoadError(true);
        return [];
      }

      const patients = await response.json();
      return patients.Items || [];
    } catch (_error) {
      setPatientLoadError(true);
      return [];
    }
  };

  const loadPatientCards = async (data) => {
    await Promise.all(
      data.map(async (element) => {
        await getPatientData(element["id"], element["patientId"]);
        await getWaitlistByPatientID(element["patientId"]);
      })
    );
  };

  async function onDelete(dynamoId, hasPendingAppt, onWaitlist) {
    if (hasPendingAppt || onWaitlist) {
      Swal.fire({
        title: "Cannot delete patient card",
        text: hasPendingAppt
          ? "This patient has incomplete or unpaid appointments."
          : "This patient is currently on the waitlist. Please cancel the waitlist before deleting the patient card.",
        icon: "error",
        confirmButtonColor: confirmButtonColor,
      });
    } else {
      const result = await Swal.fire({
        title: "Are you sure you want to delete this patient?",
        showDenyButton: true,
        confirmButtonText: "Yes",
        denyButtonText: `No`,
        confirmButtonColor: confirmButtonColor,
      });
      if (result.isConfirmed) {
        DeletePatientCard(dynamoId, false);
      }
    }
  }

  async function getPatientData(id, patientId) {
    try {
      const response = await authenticatedFetch(
        `https://${process.env.REACT_APP_ENV_ACCURO_GATEWAY}.execute-api.ca-central-1.amazonaws.com/${process.env.REACT_APP_ENV}/get-patient-by-id?patientId=${patientId}&dynamoId=${id}`,
        {
          method: "GET",
          headers: {
            Accept: "*/*",
          },
        }
      );

      const data = await response.json();
      if (!response.ok || !data) {
        setPatientLoadError(true);
        return;
      }
      if (data.error === "PATIENT_NOT_FOUND") {
        setAccuroDeletePatient(true);
        DeletePatientCard(id, true);
        return;
      }

      const patientAppointments = await getPatientAppointments(patientId);
      data.dynamoId = id;
      data.upcomingAppt = await checkTodayAppointment(
        patientAppointments,
        patientId
      );
      data.patientAppointments = patientAppointments;

      setPatientData((prev) => [...prev, data]);

      if (patientAppointments.length > 0) {
        await getAppointmentsHistory(
          patientAppointments[0]?.appointmentId,
          patientId
        );
      }
    } catch (_error) {
      setPatientLoadError(true);
    }
  }

  async function getAppointmentsHistory(appointmentId, patientID) {
    setLockedCards(true);
    const data = await appointmentsHistory(appointmentId);
    if (!data.error && data.message !== "Internal server error") {
      const appointmentNotPaid = data.filter(
        (appointment) =>
          appointment.action_reason === "Not Paid" &&
          appointment.action_type === "CustomStatusAdded"
      );
      const appointmentPaid = data.filter(
        (appointment) =>
          appointment.action_reason === "Not Paid" &&
          appointment.action_type === "CustomStatusRemoved"
      );

      if (appointmentNotPaid.length > 0 && appointmentPaid.length === 0) {
        setUnpaidAppointments((prev) => [...prev, appointmentId]);
        setUnpaidAppointmentsAlert((prev) => [...prev, patientID]);
      } else if (appointmentPaid.length > 0) {
        setPaidAppointments((prev) => [...prev, appointmentId]);
      }
    }
    setLockedCards(false);
  }

  async function checkTodayAppointment(patientAppointments, patientId) {
    if (patientAppointments.length > 0) {
      const todayAppointments = patientAppointments.filter(
        (appointment) =>
          moment(appointment.date).isSame(today, "day") ||
          moment(appointment.date).isSame(tomorrow, "day")
      );
      if (todayAppointments.length > 0) {
        setTodayAppointmentAlert((prev) => [...prev, patientId]);

        const appointmentDate = DateTime.fromISO(
          todayAppointments[0].date
        ).toFormat("cccc, LLLL dd yyyy");
        const appointmentTime = formatTimeWithPeriod(
          todayAppointments[0].startTime
        );
        return { date: appointmentDate, time: appointmentTime };
      }
    }
    return null;
  }
  async function getWaitlistByPatientID(patientId) {
    const data = await fetchWaitlistByPatientID(patientId);
    if (data.contents && data.contents.length > 0) {
      const activeWaitlist = data.contents.filter(
        (waitlist) => waitlist.caseState === "Active"
      );
      if (activeWaitlist.length > 0) {
        setWaitlistData((prevData) => [...prevData, ...activeWaitlist]);
        setActiveWaitListStatus((prev) => [...prev, patientId]);
      }
    }
  }

  const handleClose = () => {
    setAccuroDeletePatient(false);
  };

  const EmailLink = () => (
    <Link href={`mailto:${supportEmail}`} color="inherit" underline="always">
      {supportEmail}
    </Link>
  );

  return (
    <Authenticator>
      <Snackbar
        open={accuroDeletePatient}
        autoHideDuration={10000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity="error">
          <AlertTitle>Failed to fetch data for patient</AlertTitle>
          Unable to retrieve patient information. Please try creating a new
          patient profile for any missing patient cards. If this continues,
          please email <EmailLink />.
        </Alert>
      </Snackbar>
      <Box sx={{ mx: "auto", width: { xs: "90%", md: "80%" } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{
            py: 2,
            mx: "auto",
            width: { sm: "90%", md: "80%", lg: "70%" },
          }}
          alignItems="center"
          justifyContent="space-around"
        >
          {/* Info column */}
          <Stack sx={{ flex: 2, width: "100%" }}>
            <Typography sx={styles.typographyTitle}>
              SAME DAY OR NEXT DAY APPOINTMENTS
            </Typography>
            <Typography sx={styles.leftAlignedTypographyBody}>
              <b>After booking:</b> your appointment will appear on your patient
              profile. If you provided a cell number, you’ll also get a text
              confirmation. If no times are available, you can join today’s
              waitlist between 6 AM and 6 PM.
            </Typography>
          </Stack>
          {/* Button column */}
          <Stack sx={{ flex: 1, width: "100%" }} alignItems="center">
            <Stack direction="column" spacing={1}>
              <CustomButton
                onClick={() => navigate("/startForm")}
                startIcon={<PersonAdd />}
                label="Create New Patient Profile"
                sx={styles.homeButtons}
              />
              <CustomButton
                onClick={() => setShowImportantInfo(true)}
                label="Read This Before You Book"
                sx={styles.homeButtons}
                type="secondary"
              />
            </Stack>
          </Stack>
        </Stack>
        {loading && <Loading isGeneric />}
        {!loading && (
          <Stack sx={{ width: "100%", alignItems: "center" }}>
            {patientLoadError ? (
              <Alert severity="error" sx={styles.alertHome}>
                <AlertTitle sx={{ fontWeight: "bold" }}>
                  Unable to load patients
                </AlertTitle>
                We couldn’t load the patients’ information. Please refresh the
                page or sign out and sign back in. If this continues, please
                email <EmailLink />.
              </Alert>
            ) : patientData.length ? (
              <Stack sx={styles.patientStack}>
                <Typography
                  id="registered-patients"
                  sx={styles.typographyTitle}
                >
                  REGISTERED PATIENTS
                </Typography>
                <Stack direction="row" sx={styles.centeredStack}>
                  {patientData.map((data, i) => (
                    <PatientCard
                      key={i}
                      data={data}
                      onDelete={onDelete}
                      todayAppointmentAlert={todayAppointmentAlert}
                      unpaidAppointmentsAlert={unpaidAppointmentsAlert}
                      lockedCards={lockedCards}
                      tomorrow={tomorrow}
                      unpaidAppointments={unpaidAppointments}
                      paidAppointments={paidAppointments}
                      waitlistData={waitlistData}
                      activeWaitListStatus={activeWaitListStatus}
                    />
                  ))}
                </Stack>
              </Stack>
            ) : (
              <Box sx={styles.noPatientsBox}>
                <Stack spacing={2} alignItems="center">
                  <Box sx={styles.iconBox}>
                    <PersonAdd sx={{ fontSize: 36, color: darkBlue }} />
                  </Box>
                  <Stack spacing={0.5}>
                    <Typography sx={styles.typographySubtitle}>
                      No registered patients yet
                    </Typography>
                    <Typography sx={styles.typographyBody}>
                      Create your first patient profile to book same-day or
                      next-day appointments and track status in one place.
                    </Typography>
                  </Stack>
                  <Divider flexItem role="separator" />
                  <Stack spacing={1} sx={{ width: "100%", textAlign: "left" }}>
                    {[
                      "Save time booking and tracking visits for all family members.",
                      "Get text confirmations when a cell number is on file.",
                      "If no slots are available, join the daily waitlist between 6 AM and 6 PM.",
                    ].map((text) => (
                      <Stack key={text} direction="row" spacing={1}>
                        <CheckCircleOutlineOutlined
                          sx={{ color: darkBlue, fontSize: 20 }}
                        />
                        <Typography sx={styles.leftAlignedTypographyBody}>
                          {text}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                  <CustomButton
                    onClick={() => navigate("/startForm")}
                    startIcon={<PersonAdd />}
                    label="Create New Patient Profile"
                    sx={{ ...styles.homeButtons, maxWidth: "100%" }}
                  />
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Box>
      <SwipeableDrawer
        anchor="right"
        open={showImportantInfo}
        onOpen={() => setShowImportantInfo(true)}
        onClose={() => setShowImportantInfo(false)}
        transitionDuration={{ enter: 500, exit: 500 }}
        PaperProps={{ sx: { width: "100%" } }}
      >
        <Header />
        <Stack justifyContent="space-between" height="100%">
          <ImportantInformation
            onBook={() => {
              setShowImportantInfo(false);
              setTimeout(() => {
                document
                  .getElementById("registered-patients")
                  ?.scrollIntoView({ behavior: "smooth" });
              }, 0);
            }}
          />
          <Footer />
        </Stack>
      </SwipeableDrawer>
    </Authenticator>
  );
}

export default Home;
